/**
 * Card di un titolo scoperto (SPEC-015 §8).
 *
 * Due stati ben distinti, e la distinzione è la regola non negoziabile della
 * feature: una riga **identificata** ha un `instrumentId` e quindi il link al
 * dettaglio e il pulsante watchlist; una riga incerta non ha né l'uno né
 * l'altro, perché non esiste uno strumento a cui puntare. Nessun ticker uscito
 * da un modello diventa cliccabile senza essere passato dalla risoluzione.
 *
 * La motivazione è testo generato da un LLM con dentro frammenti di pagine
 * web: passa **sempre** da `Markdown` (marked + dompurify), mai in
 * `dangerouslySetInnerHTML`.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card } from '@/ui';
import { Markdown } from '@/ui/Markdown';
import { formatCompactCurrency, formatCurrency, formatDate, formatDateTime, formatPercent, signOf } from '@/lib/format';
import { useAddWatchlistItem } from '@/features/watchlist/api';
import { formatExchangeCode } from '@/features/watchlist/openfigi-exchange-codes';
import { CATEGORY_LABEL, CATEGORY_TONE, RESOLUTION_LABEL } from './discovery-labels';
import { DiscoveryReconcileModal } from './DiscoveryReconcileModal';
import { useMarkResult, type DiscoveryResult } from './api';

type Props = { runId: string; result: DiscoveryResult };

export function DiscoveryResultCard({ runId, result }: Props) {
  const addToWatchlist = useAddWatchlistItem();
  const markResult = useMarkResult(runId);
  const [reconciling, setReconciling] = useState(false);
  const actionable = result.resolutionStatus === 'resolved' && result.instrumentId !== null;
  const inWatchlist = result.addedToWatchlistAt !== null;

  const handleAdd = () => {
    if (!result.instrumentId) return;
    addToWatchlist.mutate(
      { instrumentId: result.instrumentId },
      // La marcatura segue l'aggiunta vera: marcarla prima mostrerebbe «in
      // watchlist» anche quando l'aggiunta è fallita.
      { onSuccess: () => markResult.mutate({ resultId: result.id, addedToWatchlist: true }) },
    );
  };

  return (
    <Card flush>
      <article className={`discovery-card ${actionable ? '' : 'discovery-card--muted'}`}>
        <header className="discovery-card__head">
          <div className="discovery-card__identity">
            <h3 className="discovery-card__name">
              {actionable ? (
                <Link to={`/instruments/${result.instrumentId}`}>{result.displayName}</Link>
              ) : (
                result.displayName
              )}
            </h3>
            <p className="discovery-card__meta cell--minor">
              {[result.symbol, result.mic, result.assetType].filter(Boolean).join(' · ') || 'Nessun identificativo'}
            </p>
          </div>
          <div className="discovery-card__badges">
            <Badge tone={CATEGORY_TONE[result.category]}>{CATEGORY_LABEL[result.category]}</Badge>
            {result.relevanceScore !== null && <Badge tone="neutral">{result.relevanceScore}/100</Badge>}
            {!actionable && <Badge tone="warning">{RESOLUTION_LABEL[result.resolutionStatus]}</Badge>}
          </div>
        </header>

        {!actionable && (
          <div className="discovery-card__note">
            <p className="discovery-card__note-text">
              {result.resolutionNote ?? 'Non è stato possibile collegarlo con certezza a un titolo quotato.'}
            </p>
            {/* I candidati trovati mostrati in breve: sono il lavoro di ricerca
                già fatto, e senza di essi la riga sarebbe un nome e basta. */}
            {result.mappingCandidates.length > 0 && (
              <p className="discovery-card__note-detail cell--minor">
                {summarizeCandidates(result.mappingCandidates)}
              </p>
            )}
            <Button variant="secondary" onClick={() => setReconciling(true)}>
              Identifica questo titolo
            </Button>
          </div>
        )}

        <div className="discovery-card__rationale">
          <Markdown content={result.rationale} />
        </div>

        {result.risks && (
          <p className="discovery-card__risks">
            <strong>Da tenere presente:</strong> {result.risks}
          </p>
        )}

        {/* I quattro numeri con cui due candidati si confrontano davvero
            (F-073). La VALUTA arriva dal backend e denomina sia il prezzo sia
            la capitalizzazione: prima era scritta a mano qui — euro per il
            primo, dollari per la seconda — e su un titolo di Londra
            entrambe erano sbagliate. Un dato assente resta «n/d»: per un
            titolo appena scoperto è la norma, e uno zero lo farebbe sembrare
            un crollo. */}
        <dl className="discovery-card__facts">
          <div>
            <dt>Ultimo prezzo</dt>
            <dd className="numeric">
              {result.lastPrice ? formatCurrency(result.lastPrice, result.quoteCurrency ?? undefined) : 'n/d'}
            </dd>
          </div>
          <div>
            <dt>Var. 24h</dt>
            <dd className={result.change24h ? `numeric text-${signOf(result.change24h)}` : 'numeric'}>
              {result.change24h ? formatPercent(result.change24h) : 'n/d'}
            </dd>
          </div>
          <div>
            <dt>Var. 1 mese</dt>
            <dd className={result.change30d ? `numeric text-${signOf(result.change30d)}` : 'numeric'}>
              {result.change30d ? formatPercent(result.change30d) : 'n/d'}
            </dd>
          </div>
          <div>
            <dt>Var. 1 anno</dt>
            {/* Accanto alla mensile e non al posto: una dice come si muove
                adesso, l'altra se ha una direzione — e per confrontare
                candidati fra loro e' la seconda a distinguere un titolo che
                sale da un anno da uno che ha avuto una settimana buona. */}
            <dd className={result.change1y ? `numeric text-${signOf(result.change1y)}` : 'numeric'}>
              {result.change1y ? formatPercent(result.change1y) : 'n/d'}
            </dd>
          </div>
          <div>
            <dt>Capitalizzazione</dt>
            {/* Abbreviata: qui conta l'ordine di grandezza, non le unità. */}
            <dd className="numeric">
              {result.marketCap ? formatCompactCurrency(result.marketCap, result.quoteCurrency ?? undefined) : 'n/d'}
            </dd>
          </div>
        </dl>

        {/* Distingue «mai scaricate» da «scaricate, il provider non sapeva»:
            senza, quattro «n/d» non direbbero quale dei due casi sia. */}
        {actionable && (
          <p className="discovery-card__quotes-age cell--minor">
            {result.quotesUpdatedAt
              ? `Quotazioni aggiornate al ${formatDateTime(result.quotesUpdatedAt)}`
              : 'Quotazioni non ancora scaricate per questo titolo.'}
          </p>
        )}

        {result.sources.length > 0 && (
          <div className="discovery-card__sources">
            <h4>Fonti</h4>
            <ul>
              {result.sources.map((source) => (
                <li key={source.url}>
                  {/* `noopener noreferrer` sempre: sono pagine di terzi. */}
                  <a href={source.url} target="_blank" rel="noopener noreferrer" title={source.url}>
                    {shortenUrl(source.url)}
                  </a>
                  {source.date && <span className="cell--minor"> · {formatDate(source.date)}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {actionable && (
          <footer className="discovery-card__actions">
            {inWatchlist ? (
              <Badge tone="positive">In watchlist ✓</Badge>
            ) : (
              <Button
                variant="secondary"
                onClick={handleAdd}
                disabled={addToWatchlist.isPending}
              >
                {addToWatchlist.isPending ? 'Aggiungo…' : '+ Watchlist'}
              </Button>
            )}
            <Link className="btn btn--ghost" to={`/instruments/${result.instrumentId}`}>
              Vedi dettagli e quotazioni
            </Link>
          </footer>
        )}

        {addToWatchlist.isError && (
          <p className="discovery-card__error">
            Non è stato possibile aggiungerlo alla watchlist. Riprova.
          </p>
        )}

        {reconciling && (
          <DiscoveryReconcileModal runId={runId} result={result} onClose={() => setReconciling(false)} />
        )}
      </article>
    </Card>
  );
}

/**
 * Le borse trovate, in una riga. Mostra le prime tre per nome leggibile e
 * conta le altre: l'elenco intero sta nel modal, qui serve solo capire se vale
 * la pena aprirlo.
 */
function summarizeCandidates(candidates: DiscoveryResult['mappingCandidates']): string {
  const exchanges = [...new Set(candidates.map((candidate) => formatExchangeCode(candidate.exchange)))];
  const shown = exchanges.slice(0, 3).join(', ');
  const rest = exchanges.length - 3;
  const currencies = [...new Set(candidates.map((candidate) => candidate.currency).filter(Boolean))];
  const tickers = [...new Set(candidates.map((candidate) => candidate.ticker))].slice(0, 3).join(', ');
  return [
    `${candidates.length} quotazioni trovate su ${exchanges.length} borse`,
    shown ? `(${shown}${rest > 0 ? ` e altre ${rest}` : ''})` : null,
    tickers ? `· ticker: ${tickers}` : null,
    currencies.length > 0 ? `· valute: ${currencies.join('/')}` : null,
  ]
    .filter(Boolean)
    .join(' ');
}

/** URL leggibile: il dominio e poco altro, con l'indirizzo intero nel title. */
function shortenUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.length > 24 ? `${parsed.pathname.slice(0, 24)}…` : parsed.pathname;
    return `${parsed.hostname}${path === '/' ? '' : path}`;
  } catch {
    return url.slice(0, 40);
  }
}
